/* tslint:disable */
/* eslint-disable */
/**
 * UpContent Platform API
 * # Introduction  The UpContent Public API provides access to UpContent's powerful content curation platform, allowing developers to build powerful content discovery and curation features directly into their applications.  # Authentication  The UpContent Public API uses OAuth2 authorization to grant applications access to UpContent user resources. A client application may request access to a single UpContent account or allow users to authorize access by that client to their UpContent resources.  ## User Authorization  UpContent uses a compliant OAuth2 flow to allow users to authorize access to their resources from third-party applications.  This implies that the access token must be requested from an authenticated client that can maintain the privacy of its credentials. In practice, this means that the access token must be requested from a server, rather than a JavaScript or mobile application.   ### Choosing a Grant Type The `authorization_code` is for applications intended to be use by multiple accounts where an user will grant access to their UpContent account to your application.  The `client_credentials` grant is designed for easy access to your own account without need for a full OAuth2 user authorization grant flow.  ### Choosing a Valid Scope  The UpContent API provides authorization scopes for read and write access based on the type of authorization grant requested. A valid scope must be chosen from the following:  Scope          | Grants                                     | Description ---------------|--------------------------------------------|------------ `client.read`  | `client_credentials`, `authorization_code` | Provides access to most public GET methods outlined in the documentation. `client.write` | `client_credentials`, `authorization_code` | Provides access to most public PUT, POST, and DElETE methods outlined in the documentation. `client.all`   | `client_credentials`, `authorization_code` | Alias for `client.read client.write`. `{module}.read`  | `authorization_code`                       | Provides access to a specific module's public GET methods outlined in the documentation. `{module}.write` | `authorization_code`                       | Provides access to a specific module's public PUT, POST, and DElETE methods outlined in the documentation. `{module}.all`   | `authorization_code`                       | Alias for `{module}.read {module}.write`.  Where `{module}` is one of the following: - team - folders - assignments - subscription - user - collections - collection_content - accounts - topics - topic_content - galleries - templates - quality  Currently module scopes are by special request only. By default when your client credentials are created they are given permission to use client.all.   ### Request Authorization  Begin by redirecting the user to the UpContent OAuth2 authorization page via the following request, with the querystring specified:  ``` GET https://accounts.upcontent.com/authorize  client_id={client_id}& response_type=code& grant_type=authorization_code& state={state}& redirect_uri={redirect_url}& scope={scope} ```  Parameter      | Description ---------------|--------------------------------------------------------------------- `client_id`    | The client identifier assigned to your client by UpContent. `grant_type`   | The OAuth2 grant type that is being used (`authorization_code`). `response_type`| The OAuth2 response type that is being used (`code`) `state`        | A value generated by the client that will be returned to the client to ensure that the authorization code that is returned is associated with an access token request. `redirect_uri` | One of the URLs previously associated with your client during registration with UpContent. The authorization code will be sent back to this URL. `scope`        | A list of space-delimited permissions for which the client is requesting authorization.  An allowed value for the `scope` parameter must be chosen from the table in the section *Choosing a Valid Scope*.  Upon successful authorization, an authorization code will be returned to the given redirect URL with the `state` parameter appended to the querystring, in addition to a `code` parameter which contains the authorization code.  ### Request an Access Token  Once a client has received an authorization code, it may then request an access token and a refresh token. The access token's purpose is to authenticate the client and user to the UpContent API. The purpose of the refresh token is to retrieve a new access token without the need to request a new authorization code.  The following endpoint may be used to request an access token, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=authorization_code& code={code}& redirect_url={redirect_url}& client_id={client_id}& client_secret={client_secret} ```  Field           | Description ----------------|------------ `grant_type`    | The OAuth2 grant type used in the previous step (`authorization_code`) `code`          | The authorization code obtained in the previous step `redirect_uri`  | The redirect URL to which the code was sent `client_id`     | The client identifier assigned to your client by UpContent `client_secret` | The client secret assigned to your client by UpContent  If an access token is granted, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.   ## Client-only Authorization  The UpContent API also provides a `client_credentials` grant for clients which only need access to a private UpContent account. This grant authorizes a client and generates an access token simultaneously on provision of the credentials generated when the client is registered with UpContent.  The following endpoint may be used to request an access token with this grant type, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=client_credentials& client_id={client_id}& client_secret={client_secret}& scope={scope} ```  The fields described in the previous section have the same meaning here. The scope parameter here is optional, and if omitted will default to the value `client.all` (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.  Use of the `client_credentials` grant type can only provide access tokens to the UpContent account of the client's owner.   ## Access Token  On a successful access token request, the UpContent API will return a **200 Response** and a **JSON-encoded object** with the following properties:  Property        | Type    | Description ----------------|---------|------------ `access_token`  | string  | The token used to access the UpContent API. `token_type`    | string  | The type of token. This will be 'bearer'. `expires_in`    | integer | The number of seconds until the token is expected to expire. `refresh_token` | string  | The token used to request a new access token.  The client will be responsible for securing the `access_token` and `refresh_token`, and for updating expired access tokens (see *Refreshing Access*). Note that from the date of issuance, the `access_token` will be valid for `expires_in` seconds and the `refresh_token` will be valid for 30 days.   ## Refreshing Access  Access tokens are generated with an expiration date, after which point they will no longer grant access to the UpContent API. When an access token expires, it can be refreshed using the `refresh_token` provided through the OAuth2 `refresh_token` grant.  This is accomplished making the following request, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=refresh_token& refresh_token={refresh_token}& scope={scope} ```  The fields described in the previous section have the same meaning here; the field `refresh_token` should be set to the refresh token provided at the time the expired access token was provided. The scope parameter here is optional; if provided, it must be a equal to or a subset of the scope of the expired access token, and if omitted will default to the full scope of the expired token (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a new access and refresh token in the format defined above.  If both the access and refresh tokens for a user expire, the client must reobtain authorization from that user following the procedure described in *User Authorization*.   ## Accessing the UpContent API  Upon grant of an access token, it can be used until expiration to request data from or send data to the UpContent API endpoints with scopes for which the client is authorized. This is done by including the header ``` Authorization: Bearer {token} ``` in requests to the API, where {token} represents the access token. 
 *
 * OpenAPI spec version: 2.0.0
 * 
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import globalAxios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Configuration } from '../configuration';
// Some imports not used depending on template conditions
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, RequestArgs, BaseAPI, RequiredError } from '../base';
import { CategoryIdNameBody } from '../models';
import { CategoryIdQueriesBody } from '../models';
import { InlineResponse20125 } from '../models';
import { InlineResponse20126 } from '../models';
import { WixCategoriesBody } from '../models';
import { WixCategorySet } from '../models';
/**
 * CategoriesApi - axios parameter creator
 * @export
 */
export const CategoriesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Delete a custom category
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixCategoriesCategoryIdDelete: async (category_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'category_id' is not null or undefined
            if (category_id === null || category_id === undefined) {
                throw new RequiredError('category_id','Required parameter category_id was null or undefined when calling wixCategoriesCategoryIdDelete.');
            }
            const localVarPath = `/wix/categories/{category_id}`
                .replace(`{${"category_id"}}`, encodeURIComponent(String(category_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a category
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixCategoriesCategoryIdGet: async (category_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'category_id' is not null or undefined
            if (category_id === null || category_id === undefined) {
                throw new RequiredError('category_id','Required parameter category_id was null or undefined when calling wixCategoriesCategoryIdGet.');
            }
            const localVarPath = `/wix/categories/{category_id}`
                .replace(`{${"category_id"}}`, encodeURIComponent(String(category_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the category name
         * @param {CategoryIdNameBody} body New category name
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixCategoriesCategoryIdNamePut: async (body: CategoryIdNameBody, category_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling wixCategoriesCategoryIdNamePut.');
            }
            // verify required parameter 'category_id' is not null or undefined
            if (category_id === null || category_id === undefined) {
                throw new RequiredError('category_id','Required parameter category_id was null or undefined when calling wixCategoriesCategoryIdNamePut.');
            }
            const localVarPath = `/wix/categories/{category_id}/name`
                .replace(`{${"category_id"}}`, encodeURIComponent(String(category_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the category queries
         * @param {CategoryIdQueriesBody} body New category queries
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixCategoriesCategoryIdQueriesPut: async (body: CategoryIdQueriesBody, category_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling wixCategoriesCategoryIdQueriesPut.');
            }
            // verify required parameter 'category_id' is not null or undefined
            if (category_id === null || category_id === undefined) {
                throw new RequiredError('category_id','Required parameter category_id was null or undefined when calling wixCategoriesCategoryIdQueriesPut.');
            }
            const localVarPath = `/wix/categories/{category_id}/queries`
                .replace(`{${"category_id"}}`, encodeURIComponent(String(category_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get categories by source
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixCategoriesGet: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/wix/categories`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Create a wix category
         * @param {WixCategoriesBody} body Wix category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixCategoriesPost: async (body: WixCategoriesBody, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling wixCategoriesPost.');
            }
            const localVarPath = `/wix/categories`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Deactivate a category for a component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete: async (instance_id: string, component_id: string, category_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'instance_id' is not null or undefined
            if (instance_id === null || instance_id === undefined) {
                throw new RequiredError('instance_id','Required parameter instance_id was null or undefined when calling wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete.');
            }
            // verify required parameter 'component_id' is not null or undefined
            if (component_id === null || component_id === undefined) {
                throw new RequiredError('component_id','Required parameter component_id was null or undefined when calling wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete.');
            }
            // verify required parameter 'category_id' is not null or undefined
            if (category_id === null || category_id === undefined) {
                throw new RequiredError('category_id','Required parameter category_id was null or undefined when calling wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete.');
            }
            const localVarPath = `/wix/{instance_id}/{component_id}/active-categories/{category_id}`
                .replace(`{${"instance_id"}}`, encodeURIComponent(String(instance_id)))
                .replace(`{${"component_id"}}`, encodeURIComponent(String(component_id)))
                .replace(`{${"category_id"}}`, encodeURIComponent(String(category_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Activate a category for a component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixInstanceIdComponentIdActiveCategoriesCategoryIdPut: async (instance_id: string, component_id: string, category_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'instance_id' is not null or undefined
            if (instance_id === null || instance_id === undefined) {
                throw new RequiredError('instance_id','Required parameter instance_id was null or undefined when calling wixInstanceIdComponentIdActiveCategoriesCategoryIdPut.');
            }
            // verify required parameter 'component_id' is not null or undefined
            if (component_id === null || component_id === undefined) {
                throw new RequiredError('component_id','Required parameter component_id was null or undefined when calling wixInstanceIdComponentIdActiveCategoriesCategoryIdPut.');
            }
            // verify required parameter 'category_id' is not null or undefined
            if (category_id === null || category_id === undefined) {
                throw new RequiredError('category_id','Required parameter category_id was null or undefined when calling wixInstanceIdComponentIdActiveCategoriesCategoryIdPut.');
            }
            const localVarPath = `/wix/{instance_id}/{component_id}/active-categories/{category_id}`
                .replace(`{${"instance_id"}}`, encodeURIComponent(String(instance_id)))
                .replace(`{${"component_id"}}`, encodeURIComponent(String(component_id)))
                .replace(`{${"category_id"}}`, encodeURIComponent(String(category_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get active categories by source
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixInstanceIdComponentIdActiveCategoriesGet: async (instance_id: string, component_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'instance_id' is not null or undefined
            if (instance_id === null || instance_id === undefined) {
                throw new RequiredError('instance_id','Required parameter instance_id was null or undefined when calling wixInstanceIdComponentIdActiveCategoriesGet.');
            }
            // verify required parameter 'component_id' is not null or undefined
            if (component_id === null || component_id === undefined) {
                throw new RequiredError('component_id','Required parameter component_id was null or undefined when calling wixInstanceIdComponentIdActiveCategoriesGet.');
            }
            const localVarPath = `/wix/{instance_id}/{component_id}/active-categories`
                .replace(`{${"instance_id"}}`, encodeURIComponent(String(instance_id)))
                .replace(`{${"component_id"}}`, encodeURIComponent(String(component_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * CategoriesApi - functional programming interface
 * @export
 */
export const CategoriesApiFp = function(configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Delete a custom category
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesCategoryIdDelete(category_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixCategoriesCategoryIdDelete(category_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get a category
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesCategoryIdGet(category_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<WixCategorySet>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixCategoriesCategoryIdGet(category_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the category name
         * @param {CategoryIdNameBody} body New category name
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesCategoryIdNamePut(body: CategoryIdNameBody, category_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20125>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixCategoriesCategoryIdNamePut(body, category_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the category queries
         * @param {CategoryIdQueriesBody} body New category queries
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesCategoryIdQueriesPut(body: CategoryIdQueriesBody, category_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20126>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixCategoriesCategoryIdQueriesPut(body, category_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get categories by source
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesGet(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<WixCategorySet>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixCategoriesGet(options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Create a wix category
         * @param {WixCategoriesBody} body Wix category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesPost(body: WixCategoriesBody, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<WixCategorySet>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixCategoriesPost(body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Deactivate a category for a component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete(instance_id: string, component_id: string, category_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete(instance_id, component_id, category_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Activate a category for a component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdActiveCategoriesCategoryIdPut(instance_id: string, component_id: string, category_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixInstanceIdComponentIdActiveCategoriesCategoryIdPut(instance_id, component_id, category_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get active categories by source
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdActiveCategoriesGet(instance_id: string, component_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<WixCategorySet>>> {
            const localVarAxiosArgs = await CategoriesApiAxiosParamCreator(configuration).wixInstanceIdComponentIdActiveCategoriesGet(instance_id, component_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
    }
};

/**
 * CategoriesApi - factory interface
 * @export
 */
export const CategoriesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    return {
        /**
         * 
         * @summary Delete a custom category
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesCategoryIdDelete(category_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return CategoriesApiFp(configuration).wixCategoriesCategoryIdDelete(category_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a category
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesCategoryIdGet(category_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<WixCategorySet>> {
            return CategoriesApiFp(configuration).wixCategoriesCategoryIdGet(category_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the category name
         * @param {CategoryIdNameBody} body New category name
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesCategoryIdNamePut(body: CategoryIdNameBody, category_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20125>> {
            return CategoriesApiFp(configuration).wixCategoriesCategoryIdNamePut(body, category_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the category queries
         * @param {CategoryIdQueriesBody} body New category queries
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesCategoryIdQueriesPut(body: CategoryIdQueriesBody, category_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20126>> {
            return CategoriesApiFp(configuration).wixCategoriesCategoryIdQueriesPut(body, category_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get categories by source
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesGet(options?: AxiosRequestConfig): Promise<AxiosResponse<WixCategorySet>> {
            return CategoriesApiFp(configuration).wixCategoriesGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Create a wix category
         * @param {WixCategoriesBody} body Wix category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixCategoriesPost(body: WixCategoriesBody, options?: AxiosRequestConfig): Promise<AxiosResponse<WixCategorySet>> {
            return CategoriesApiFp(configuration).wixCategoriesPost(body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Deactivate a category for a component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete(instance_id: string, component_id: string, category_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return CategoriesApiFp(configuration).wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete(instance_id, component_id, category_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Activate a category for a component
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {string} category_id UUID for the category
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdActiveCategoriesCategoryIdPut(instance_id: string, component_id: string, category_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return CategoriesApiFp(configuration).wixInstanceIdComponentIdActiveCategoriesCategoryIdPut(instance_id, component_id, category_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get active categories by source
         * @param {string} instance_id UUID for the Wix instance
         * @param {string} component_id Unique identifier for the Wix component
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixInstanceIdComponentIdActiveCategoriesGet(instance_id: string, component_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<WixCategorySet>> {
            return CategoriesApiFp(configuration).wixInstanceIdComponentIdActiveCategoriesGet(instance_id, component_id, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * CategoriesApi - object-oriented interface
 * @export
 * @class CategoriesApi
 * @extends {BaseAPI}
 */
export class CategoriesApi extends BaseAPI {
    /**
     * 
     * @summary Delete a custom category
     * @param {string} category_id UUID for the category
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixCategoriesCategoryIdDelete(category_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return CategoriesApiFp(this.configuration).wixCategoriesCategoryIdDelete(category_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get a category
     * @param {string} category_id UUID for the category
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixCategoriesCategoryIdGet(category_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<WixCategorySet>> {
        return CategoriesApiFp(this.configuration).wixCategoriesCategoryIdGet(category_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the category name
     * @param {CategoryIdNameBody} body New category name
     * @param {string} category_id UUID for the category
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixCategoriesCategoryIdNamePut(body: CategoryIdNameBody, category_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20125>> {
        return CategoriesApiFp(this.configuration).wixCategoriesCategoryIdNamePut(body, category_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the category queries
     * @param {CategoryIdQueriesBody} body New category queries
     * @param {string} category_id UUID for the category
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixCategoriesCategoryIdQueriesPut(body: CategoryIdQueriesBody, category_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20126>> {
        return CategoriesApiFp(this.configuration).wixCategoriesCategoryIdQueriesPut(body, category_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get categories by source
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixCategoriesGet(options?: AxiosRequestConfig) : Promise<AxiosResponse<WixCategorySet>> {
        return CategoriesApiFp(this.configuration).wixCategoriesGet(options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Create a wix category
     * @param {WixCategoriesBody} body Wix category
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixCategoriesPost(body: WixCategoriesBody, options?: AxiosRequestConfig) : Promise<AxiosResponse<WixCategorySet>> {
        return CategoriesApiFp(this.configuration).wixCategoriesPost(body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Deactivate a category for a component
     * @param {string} instance_id UUID for the Wix instance
     * @param {string} component_id Unique identifier for the Wix component
     * @param {string} category_id UUID for the category
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete(instance_id: string, component_id: string, category_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return CategoriesApiFp(this.configuration).wixInstanceIdComponentIdActiveCategoriesCategoryIdDelete(instance_id, component_id, category_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Activate a category for a component
     * @param {string} instance_id UUID for the Wix instance
     * @param {string} component_id Unique identifier for the Wix component
     * @param {string} category_id UUID for the category
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixInstanceIdComponentIdActiveCategoriesCategoryIdPut(instance_id: string, component_id: string, category_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return CategoriesApiFp(this.configuration).wixInstanceIdComponentIdActiveCategoriesCategoryIdPut(instance_id, component_id, category_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get active categories by source
     * @param {string} instance_id UUID for the Wix instance
     * @param {string} component_id Unique identifier for the Wix component
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof CategoriesApi
     */
    public async wixInstanceIdComponentIdActiveCategoriesGet(instance_id: string, component_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<WixCategorySet>> {
        return CategoriesApiFp(this.configuration).wixInstanceIdComponentIdActiveCategoriesGet(instance_id, component_id, options).then((request) => request(this.axios, this.basePath));
    }
}
